import React, { useCallback, useMemo, useState } from 'react';
import size from 'lodash/size';
import reduce from 'lodash/reduce';
import isEmpty from 'lodash/isEmpty';

import {
  ChangeMaterialsFiltersFunc,
  FetchMaterialsCacheKey,
  FetchMaterialsCacheKeys,
  FetchMaterialsFilterFields,
  FetchMaterialsFilterMaterials,
  FetchMaterialsFilters,
  FetchMaterialsLimit
} from '../../../materials/materialsTypes';
import { TeamNanoID, TeamNda, TeamUUID } from '../../../teams/teamsTypes';
import {
  FetchMaterialsSetByUserIdScopeType,
  FetchMaterialsSetsCacheKey,
  FetchMaterialsSetsCacheKeys
} from '../../../materialsSets/materialsSetsTypes';
import { ThreeDStockOnMaterialsSelect } from './ThreeDStockMaterials.types';

import {
  FETCH_MATERIALS_QUERY,
  FetchMaterialsQueryResponse
} from '../../../materials/queries/fetchMaterials.query';
import { FETCH_SHOW_MATERIAL_QUERY } from '../../../materials/queries/fetchShowMaterial.query';
import { CACHE_FETCH_SHOW_MATERIAL_QUERY } from '../../../materials/queries/fetchCacheShowMaterial.query';
import { CACHE_FETCH_MATERIALS_QUERY } from '../../../materials/queries/fetchCacheMaterials.query';

import { useMaterialsFiltersCache } from '../../../materials/hooks/useMaterialsFiltersCache';
import { useThreeDStockMaterialsSearch } from './hooks/useThreeDStockMaterialsSearch';
import { useThreeDStockMaterialsContentCheckedCategories } from '../../components/contents/ThreeDStockMaterialsContent/hooks/useThreeDStockMaterialsContentCheckedCategories';
import { useMaterialsIndexPageSelectedMaterials } from '../../../materials/pages/MaterialsIndexPage/hooks/useMaterialsIndexPageSelectedMaterials';
import { useCacheMaterials } from '../../../materials/hooks/useCacheMaterials';
import { useChangePathQueryFilters } from '../../hooks/useChangePathQueryFilters';

import { ThreeDStockShowLayout } from '../../components/layouts/ThreeDStockShowLayout';

import { SelectedMaterialsSidebar } from '../../../selectedMaterials/components/sidebars/SelectedMaterialsSidebar';
import { SelectedMaterialsFloatingCounter } from '../../../selectedMaterials/components/SelectedMaterialsFloatingCounter';

import { ThreeDStockHeader } from '../../components/headers/ThreeDStockHeader';
import { ThreeDStockSubHeader } from '../../components/headers/ThreeDStockSubHeader';
import { ThreeDStockMaterialsContent } from '../../components/contents/ThreeDStockMaterialsContent';
import { ThreeDStockMenu } from '../../components/menus/ThreeDStockMenu';
import { ThreeDStockModalHeader } from '../../components/headers/ThreeDStockModalHeader';

import { MaterialCache } from '../../../materials/MaterialCache';

import { StockTabs } from '../../stockConstants';
import { TeamPath } from '../../../teams/TeamPath';
import { StockPath } from '../../StockPath';

const defaultInitialLimit = 48;

const categoriesFilterFields = [
  FetchMaterialsFilterFields.MATERIAL_CATEGORY_IDS,
  FetchMaterialsFilterFields.MATERIAL_PARENT_CATEGORY_IDS
];

interface ThreeDStockMaterialsModalProps {
  pageAction?: never;
  isModal: boolean;
}

interface ThreeDStockMaterialsPageProps {
  pageAction: string;
  isModal?: never;
}

interface ThreeDStockMaterialsDefaultProps {
  materialsCacheKey: FetchMaterialsCacheKey;
  initialFilters?: FetchMaterialsFilters;
  initialLimit?: FetchMaterialsLimit;
  materialsSetCacheKey: FetchMaterialsSetsCacheKey;
  materialsSetScope?: FetchMaterialsSetByUserIdScopeType;
  isMyLibrary?: boolean;
  companyNanoId?: TeamNanoID;
  companyUuid?: TeamUUID;
  onMaterialsAttach?: ThreeDStockOnMaterialsSelect;
  onMaterialsSelect?: ThreeDStockOnMaterialsSelect;
  onChangeFilters?: (filters?: FetchMaterialsFilters) => void;
  nda?: TeamNda;
}

type ThreeDStockMaterialsProps = ThreeDStockMaterialsDefaultProps &
  (ThreeDStockMaterialsModalProps | ThreeDStockMaterialsPageProps);

function ThreeDStockMaterials({
  pageAction,
  isModal,
  materialsCacheKey,
  initialFilters,
  initialLimit = defaultInitialLimit,
  materialsSetCacheKey,
  materialsSetScope,
  companyNanoId,
  companyUuid,
  isMyLibrary,
  onMaterialsAttach,
  onMaterialsSelect,
  onChangeFilters,
  nda
}: ThreeDStockMaterialsProps) {
  const [isOpenMenu, setIsOpenMenu] = useState(false);

  const handleMenuToggle = useCallback<() => void>(
    () => setIsOpenMenu((prev) => !prev),
    []
  );

  const materialsCacheKeys = useMemo<FetchMaterialsCacheKeys>(
    () => [materialsCacheKey],
    [materialsCacheKey]
  );
  const materialsSetCacheKeys = useMemo<FetchMaterialsSetsCacheKeys>(
    () => [materialsSetCacheKey],
    [materialsSetCacheKey]
  );

  const {
    defaultInitialFilters,
    cachedMaterialsSort,
    cachedMaterialsFilters,
    changeCachedMaterialsFilters,
    setCachedMaterialsFilters,
    resetCachedMaterialsFiltersAndSort
  } = useMaterialsFiltersCache({
    cacheKey: materialsCacheKey,
    initialFilters,
    ignoredFilterNames: []
  });

  const {
    materials,
    materialsError,
    materialsTotalCount,
    materialsFetched,
    materialsFetchingNextPage,
    materialsFilters,
    materialsIsPlaceholderData,
    hasNextMaterialsPage,
    updateMaterialCache,
    loadMoreMaterials,
    prefetchMaterial,
    changeMaterialsFilters,
    materialsSort,
    sortMaterials,
    filterMaterials,
    materialsPage,
    materialsLimit
  } = useCacheMaterials<FetchMaterialsQueryResponse>({
    cacheKey: materialsCacheKey,
    query: FETCH_MATERIALS_QUERY,
    cacheQuery: CACHE_FETCH_MATERIALS_QUERY,
    fetchItemCacheKey: MaterialCache.showCacheKey(),
    fetchItemQuery: FETCH_SHOW_MATERIAL_QUERY,
    fetchCacheItemQuery: CACHE_FETCH_SHOW_MATERIAL_QUERY,
    initialLimit,
    initialFilters: cachedMaterialsFilters,
    initialSort: cachedMaterialsSort,
    trackTotalHits: true
  });

  const {
    materialsSet,
    materialsSetError,
    materialsSetFetched,
    isOptimisticUpdateData,
    materialsSetIsPlaceholderData,
    updateMaterialsSetCache,
    isSelectedMaterialsSidebarOpen,
    handleSelectedMaterialsSidebarOpen,
    handleSelectedMaterialsSidebarClose
  } = useMaterialsIndexPageSelectedMaterials({
    materialsSetScope,
    materialsSetCacheKey,
    onMaterialsSelect
  });

  const { clearCheckedCategories } =
    useThreeDStockMaterialsContentCheckedCategories();

  const handleResetFilterMaterials = useCallback<() => void>(() => {
    clearCheckedCategories();
    resetCachedMaterialsFiltersAndSort(filterMaterials, sortMaterials);
  }, [
    clearCheckedCategories,
    filterMaterials,
    resetCachedMaterialsFiltersAndSort,
    sortMaterials
  ]);

  const { changePathQueryFilters } = useChangePathQueryFilters({
    currentTab: StockTabs.MATERIALS,
    companyNanoId,
    isMyLibrary,
    onIndexVisit: isModal ? undefined : handleResetFilterMaterials
  });

  const handleChangeMaterialsFilters = useCallback<ChangeMaterialsFiltersFunc>(
    (changedFilters, removeFilters) => {
      changeCachedMaterialsFilters(changedFilters, removeFilters);

      changeMaterialsFilters(changedFilters, removeFilters);

      if (
        !isEmpty(removeFilters) &&
        categoriesFilterFields.every((field) => removeFilters?.includes(field))
      ) {
        onChangeFilters?.({});

        !isModal && changePathQueryFilters(StockTabs.MATERIALS);
      }
    },
    [
      changeCachedMaterialsFilters,
      changeMaterialsFilters,
      changePathQueryFilters,
      isModal,
      onChangeFilters
    ]
  );

  const handleFilterMaterials = useCallback<FetchMaterialsFilterMaterials>(
    (nextFilters) => {
      setCachedMaterialsFilters(nextFilters);

      filterMaterials(nextFilters);
    },
    [filterMaterials, setCachedMaterialsFilters]
  );

  const handleClearFilterMaterials = useCallback<
    (persistFiltersFields?: FetchMaterialsFilterFields[]) => void
  >(
    (persistFiltersFields) => {
      const persistFilters = reduce(
        persistFiltersFields,
        (result, field) => {
          result[field] = materialsFilters?.[field];
          return result;
        },
        defaultInitialFilters
      );

      return handleFilterMaterials(persistFilters);
    },
    [defaultInitialFilters, handleFilterMaterials, materialsFilters]
  );

  const changeMaterialsCategoryFilters = useCallback<
    (tab?: StockTabs, changeFilters?: FetchMaterialsFilters) => void
  >(
    (tab, changedFilters) => {
      clearCheckedCategories();

      const searchFilters: FetchMaterialsFilters = {};
      if (materialsFilters.nameSkuTags)
        searchFilters.nameSkuTags = materialsFilters.nameSkuTags;
      if (materialsFilters.name) searchFilters.name = materialsFilters.name;

      if (tab === StockTabs.MATERIALS) {
        handleFilterMaterials({
          ...defaultInitialFilters,
          ...changedFilters,
          ...searchFilters
        });

        onChangeFilters?.({ ...changedFilters, ...searchFilters });
      }

      if (!isModal) {
        changePathQueryFilters(tab, { ...changedFilters, ...searchFilters });
      }
    },
    [
      changePathQueryFilters,
      clearCheckedCategories,
      defaultInitialFilters,
      handleFilterMaterials,
      isModal,
      onChangeFilters,
      materialsFilters
    ]
  );

  const { handleSearch } = useThreeDStockMaterialsSearch({
    changeMaterialsFilters: handleChangeMaterialsFilters
  });

  return (
    <ThreeDStockShowLayout
      isModal={isModal}
      action={pageAction}
      header={
        <ThreeDStockHeader
          tab={StockTabs.MATERIALS}
          withAddButton
          disabled={!materialsFetched}
          onSearch={handleSearch}
          value={
            materialsFilters.nameSkuTags || materialsFilters.name?.ilike || ''
          }
          isLib={!!companyNanoId}
          isMyLibrary={isMyLibrary}
          libHref={
            (isMyLibrary && TeamPath.currentCompanyNewLibraryMaterials()) ||
            (companyNanoId &&
              TeamPath.companyNewLibraryMaterials(companyNanoId)) ||
            TeamPath.currentCompanyNewLibraryMaterials()
          }
          stockHref={
            isMyLibrary || !companyNanoId ? StockPath.newMaterials() : null
          }
          team={{ uuid: companyUuid, nda }}
        />
      }
      modalHeader={
        <ThreeDStockModalHeader
          tab={StockTabs.MATERIALS}
          disabled={!materialsFetched}
          onSearch={handleSearch}
          value={
            materialsFilters.nameSkuTags || materialsFilters.name?.ilike || ''
          }
          onCategoriesClick={handleMenuToggle}
        />
      }
      subHeader={
        <ThreeDStockSubHeader
          tab={StockTabs.MATERIALS}
          companyNanoId={companyNanoId}
          isMyLibrary={isMyLibrary}
          onAllClick={handleMenuToggle}
          onSelectedItemsSidebarOpen={handleSelectedMaterialsSidebarOpen}
          selectedItemsCount={size(materialsSet?.selectedMaterials)}
        />
      }
      secondaryMenu={
        isOpenMenu ? (
          <ThreeDStockMenu
            showTab={isModal ? StockTabs.MATERIALS : null}
            onClose={handleMenuToggle}
            changeItemsCategoryFilters={changeMaterialsCategoryFilters}
            companyNanoId={companyNanoId}
            isMyLibrary={isMyLibrary}
            isModal={isModal}
            clientIds={defaultInitialFilters?.clientIds}
          />
        ) : null
      }
      sidebar={
        isSelectedMaterialsSidebarOpen ? (
          <SelectedMaterialsSidebar
            materialsSet={materialsSet}
            materialsSetFetched={materialsSetFetched}
            materialsSetError={materialsSetError}
            materialsSetIsPlaceholderData={materialsSetIsPlaceholderData}
            materialsCacheKeys={materialsCacheKeys}
            materialsSetCacheKeys={materialsSetCacheKeys}
            onClose={handleSelectedMaterialsSidebarClose}
            updateMaterialsSetCache={updateMaterialsSetCache}
            withoutBackGround={isModal}
            onCustomAttach={onMaterialsAttach}
            disabledAttach={isOptimisticUpdateData}
          />
        ) : null
      }
    >
      <ThreeDStockMaterialsContent
        materialsSort={materialsSort}
        materialsCacheKey={materialsCacheKey}
        materialsFilters={materialsFilters}
        sortMaterials={sortMaterials}
        changeMaterialsFilters={handleChangeMaterialsFilters}
        changeMaterialsCategoryFilters={changeMaterialsCategoryFilters}
        filterMaterials={handleFilterMaterials}
        materials={materials}
        onMaterialEditButtonMouseEnter={prefetchMaterial}
        materialsError={materialsError}
        materialsFetched={materialsFetched}
        materialsIsPlaceholderData={materialsIsPlaceholderData}
        materialsTotalCount={materialsTotalCount}
        hasNextMaterialsPage={hasNextMaterialsPage}
        materialsPage={materialsPage}
        materialsLimit={materialsLimit}
        loadMoreMaterials={loadMoreMaterials}
        materialsFetchingNextPage={materialsFetchingNextPage}
        materialsSet={materialsSet}
        updateMaterialsSetCache={updateMaterialsSetCache}
        materialsSetCacheKeys={materialsSetCacheKeys}
        updateMaterialCache={updateMaterialCache}
        clearMaterialsFilters={handleClearFilterMaterials}
        defaultMaterialCategoryIds={defaultInitialFilters?.materialCategoryIds}
        defaultMaterialParentCategoryIds={
          defaultInitialFilters?.materialParentCategoryIds
        }
        companyNanoId={companyNanoId}
        teamUuid={companyUuid}
        isMyLibrary={isMyLibrary}
        isModal={isModal}
      />

      {isModal &&
      materialsSetFetched &&
      materialsSet &&
      !isSelectedMaterialsSidebarOpen ? (
        <SelectedMaterialsFloatingCounter
          materialsSet={materialsSet}
          materialsSetCacheKey={materialsSetCacheKey}
          isOpen={isSelectedMaterialsSidebarOpen}
          onOpen={handleSelectedMaterialsSidebarOpen}
          onClose={handleSelectedMaterialsSidebarClose}
          updateMaterialsSetCache={updateMaterialsSetCache}
        />
      ) : null}
    </ThreeDStockShowLayout>
  );
}

export default ThreeDStockMaterials;
